import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Languages, Loader2 } from "lucide-react";

const BG_IMAGE = "https://images.unsplash.com/photo-1764314138160-5f04f4a50dae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxwaHlzaWNhbCUyMHRoZXJhcGlzdCUyMGNsaW5pY3xlbnwwfHx8fDE3NzgxMDM2NzJ8MA&ixlib=rb-4.1.0&q=85";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [invalid, setInvalid] = useState(!token);

  useEffect(() => {
    if (!token) setInvalid(true);
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (pw !== pw2) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        new_password: pw,
      });
      // The backend sets the httpOnly session cookie on the same response —
      // we just need to update local user state.
      localStorage.setItem("tt_user", JSON.stringify(data.user));
      setUser(data.user);
      toast.success("Password updated. You're signed in.");
      nav(data.user.has_active_subscription ? "/app" : "/billing");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 400) setInvalid(true);
      toast.error(detail || "Could not reset password. The link may have expired.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{ backgroundImage: `linear-gradient(rgba(255,255,255,0.92), rgba(255,255,255,0.92)), url(${BG_IMAGE})`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-xl p-8" data-testid="reset-password-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-zinc-900 flex items-center justify-center">
            <Languages className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading text-xl tracking-tight text-zinc-900">Clinico Translate</span>
        </div>

        {invalid ? (
          <div data-testid="reset-invalid">
            <h2 className="font-heading text-2xl tracking-tight text-zinc-900 mb-2">This link won't work</h2>
            <p className="text-zinc-600">It may have expired, already been used, or be malformed. Request a new one to continue.</p>
            <Button asChild className="mt-6 w-full h-12" data-testid="reset-request-new">
              <Link to="/forgot-password">Request a new reset link</Link>
            </Button>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-3xl tracking-tight text-zinc-900 mb-2">Set a new password</h2>
            <p className="text-zinc-600 mb-8">Pick something you'll remember. You'll be signed in right after.</p>
            <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
              <div>
                <Label htmlFor="pw" className="text-sm uppercase tracking-[0.2em] text-zinc-500">New password</Label>
                <PasswordInput
                  id="pw"
                  required
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="mt-2 h-12 text-base"
                  data-testid="reset-pw-input"
                  autoComplete="new-password"
                />
              </div>
              <div>
                <Label htmlFor="pw2" className="text-sm uppercase tracking-[0.2em] text-zinc-500">Confirm new password</Label>
                <PasswordInput
                  id="pw2"
                  required
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="mt-2 h-12 text-base"
                  data-testid="reset-pw2-input"
                  autoComplete="new-password"
                />
              </div>
              <Button type="submit" disabled={busy} className="w-full h-12 text-base" data-testid="reset-submit-button">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
